'use client';

/**
 * UploadOverlay
 *
 * Compact floating dark card (bottom-right) showing upload progress,
 * failure state with retry/discard, offline status, and completion.
 *
 * Mounted at the app root level (ProvidersWrapper or layout).
 * Orphan recovery is handled by UploadRecovery (upload-recovery.tsx).
 */

import { useEffect, useCallback } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  AudioLines,
  WifiOff
} from 'lucide-react';
import { toast } from '@/lib/toast';
import { useQueryClient } from '@tanstack/react-query';
import { recordingKeys } from '@/lib/query-keys';
import {
  useRecordingUploadStore,
  useRecordingUploadActions
} from '@/lib/stores/recording-upload-store';
import {
  getRecordingMeta,
  deleteRecordingMeta,
  deleteChunksForRecording,
  deleteRecordingAction
} from '@/lib/services/recordings';
import { uploadRecording } from './upload-recording';

function formatEta(seconds: number): string {
  if (seconds < 60) return `~${seconds}s remaining`;
  return `~${Math.ceil(seconds / 60)}min remaining`;
}

export function UploadOverlay() {
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const state = useRecordingUploadStore();
  const uploadActions = useRecordingUploadActions();

  // LF-3743: the mobile QR-handoff route shares this store (so it can
  // reuse the desktop upload pipeline + state) but renders its own
  // mobile-styled progress UI inline on the page. The desktop overlay
  // would render simultaneously with `matterId: 0` (the mobile sentinel)
  // and its "View" button would 404 to /matters/0/recordings. Suppress on
  // the public route — the page itself owns the upload UI there.
  const isMobileHandoffRoute = pathname?.startsWith('/device-recording');

  const hasPendingRecording = state.recordingId !== null;

  const isVisible =
    state.isUploading ||
    state.uploadFailed ||
    (state.isOffline && hasPendingRecording) ||
    state.percentComplete === 100;

  // Online/offline tracking
  useEffect(() => {
    const goOnline = () => uploadActions.setOffline(false);
    const goOffline = () => uploadActions.setOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [uploadActions]);

  const handleRetry = useCallback(async () => {
    if (!state.matterId || !state.recordingId) return;

    uploadActions.retry();

    const _meta = await getRecordingMeta(state.recordingId);
    const result = await uploadRecording(state.recordingId, (progress) => {
      uploadActions.updateProgress(progress.loaded);
    });

    if (result.success) {
      uploadActions.complete();
      queryClient.invalidateQueries({
        queryKey: recordingKeys.list(state.matterId!)
      });
      setTimeout(() => uploadActions.reset(), 5000);
    } else {
      uploadActions.setFailed();
    }
  }, [state.matterId, state.recordingId, uploadActions, queryClient]);

  const handleDiscard = useCallback(async () => {
    if (!state.matterId || !state.recordingId) return;

    // Delete server recording + local data
    await deleteRecordingAction(state.recordingId).catch(() => {});
    await deleteChunksForRecording(state.recordingId).catch(() => {});
    await deleteRecordingMeta(state.recordingId).catch(() => {});

    queryClient.invalidateQueries({
      queryKey: recordingKeys.list(state.matterId!)
    });
    uploadActions.reset();
    toast('Recording discarded');
  }, [state.matterId, state.recordingId, uploadActions, queryClient]);

  const handleNavigate = useCallback(() => {
    if (state.matterId) {
      router.push(`/matters/${state.matterId}/recordings`);
    }
  }, [state.matterId, router]);

  if (isMobileHandoffRoute) return null;
  if (!isVisible) return null;

  return (
    <div className="fixed right-4 bottom-4 z-50 w-72 rounded-lg border border-gray-700 bg-gray-900 shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-700/50 px-3 py-1.5">
        <div className="flex items-center gap-1.5">
          <AudioLines className="h-3 w-3 text-gray-500" />
          <span className="text-xs font-medium text-gray-400">AI Scribe</span>
          <span className="text-gray-600">&middot;</span>
          <button
            type="button"
            onClick={handleNavigate}
            className="inline-flex items-center gap-0.5 text-xs text-blue-400 hover:text-blue-300"
          >
            View <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-3 py-2.5">
        {state.isUploading && (
          <>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-300">Uploading...</span>
              <span className="text-xs font-medium text-gray-400">
                {state.percentComplete}%
              </span>
            </div>
            <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-gray-700">
              <div
                className="h-full rounded-full bg-blue-500 transition-[width] duration-300"
                style={{ width: `${state.percentComplete}%` }}
              />
            </div>
            <p className="text-12px mt-1.5 text-gray-500">
              {state.etaSeconds !== null
                ? `${formatEta(state.etaSeconds)} · `
                : ''}
              Don&apos;t close tab
            </p>
          </>
        )}

        {state.uploadFailed && (
          <div className="flex items-center gap-2">
            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-500/15">
              <AlertTriangle className="h-3 w-3 text-rose-400" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-medium text-gray-200">Upload failed</p>
              <p className="text-12px text-gray-500">Saved locally</p>
            </div>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={handleRetry}
                className="text-12px rounded bg-white px-2 py-1 font-medium text-gray-900"
              >
                Retry
              </button>
              <button
                type="button"
                onClick={handleDiscard}
                className="text-12px rounded bg-gray-700 px-2 py-1 font-medium text-gray-300 hover:bg-gray-600"
              >
                Discard
              </button>
            </div>
          </div>
        )}

        {state.isOffline && !state.uploadFailed && (
          <div className="flex items-center gap-2">
            <WifiOff className="h-3.5 w-3.5 shrink-0 text-gray-500" />
            <p className="text-xs text-gray-400">
              Offline — will upload on reconnect
            </p>
          </div>
        )}

        {!state.isUploading &&
          !state.uploadFailed &&
          state.percentComplete === 100 && (
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
              <p className="text-xs text-gray-300">
                Upload complete — processing
              </p>
            </div>
          )}
      </div>
    </div>
  );
}
